import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  Observable,
  Subject,
  Subscription
} from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { Page } from '../../payments/giftcards/model/page.model';
import { CompanySettingsService } from '../../settings/services/company-settings.service';
import { CompanySettings } from '../../settings/model/company-settings';

import { RpcService } from '../../shared/http/rpc.service';
import { ITEMSPERPAGE } from '../../shared/paging/items-per-page';
import { ClosingReport } from './closing-report';
import { ClosingReportDto } from './closing-report.dto';
import * as fromCompanySettingsStore from '../../settings/store';
import { Store } from '@ngrx/store';
import { TokenService } from '../../authentication/token.service';

@Injectable()
export class ClosingReportService implements OnDestroy {
  private shouldShowClosingReportSubject: BehaviorSubject<
    boolean
  > = new BehaviorSubject(false);
  private _shouldShowClosingReport$: Observable<boolean>;

  private ngUnsubscribe: Subject<Subscription> = new Subject();

  constructor(
    private rpc: RpcService,
    private tokenService: TokenService,
    private companySettings: CompanySettingsService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {
    this._shouldShowClosingReport$ = this.shouldShowClosingReportSubject.asObservable();
    this.calculateShowingClosingReports();
    this.companySettings.companySettingsChanged$.subscribe(
      (changed: boolean) => {
        if (changed) {
          this.calculateShowingClosingReports();
        }
      }
    );
  }

  public getClosingReports(
    offset: number,
    amount: number = ITEMSPERPAGE
  ): Observable<Page<ClosingReport>> {
    if (amount > ITEMSPERPAGE) {
      amount = ITEMSPERPAGE;
    }

    return this.tokenService.getToken$().pipe(
      switchMap(token =>
        this.rpc
          .makeGenericCall('posiosAccounting.getZReportMetaDatas', [
            token,
            offset,
            amount
          ])
          .pipe(
            map((closingReports: Array<ClosingReportDto>) => {
              return closingReports.map((closingReport: ClosingReportDto) => {
                return ClosingReport.fromDto(closingReport);
              });
            }),
            map((closingReports: Array<ClosingReport>) => {
              const page: Page<ClosingReport> = new Page<ClosingReport>();
              if (closingReports.length > 0) {
                page.results = closingReports;
                page.total = offset + closingReports[0].sequenceNumber;
              }
              page.requestedItemsPerPage = amount;
              page.offset = offset;
              return page;
            })
          )
      )
    );
  }

  public shouldShowClosingReports(): Observable<boolean> {
    return this._shouldShowClosingReport$;
  }

  public calculateShowingClosingReports(): void {
    combineLatest(
      fromCompanySettingsStore.getSettingsFromStore(this.store),
      this.getClosingReports(0, 1)
    )
      .pipe(
        switchMap((value: [CompanySettings, Page<ClosingReport>]) => {
          this.shouldShowClosingReportSubject.next(
            value[1].total > 0 || value[0].closingReportsEnabled
          );
          return EMPTY;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
